<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props" data-cy="list-podItem-actions-extractArchive-activator">
        <slot></slot>
      </div>
    </template>

    <v-card>
      <v-card-title class="d-flex justify-space-between" data-cy="dialog-extractArchive-title">
        {{ $t('routes.pod.extractArchive') }}

        <v-btn
          icon="mdi-close"
          variant="text"
          @click="dialog = false"
          :disabled="isRequestSubmitted"
        ></v-btn>
      </v-card-title>

      <v-form ref="form" @submit.prevent="handleFormSubmit()" data-cy="dialog-extractArchive-modal">
        <v-card-text class="pb-0">
          <div data-cy="dialog-extractArchive-content">
            {{ $t('routes.pod.extractArchiveConfirmDialog') }}
          </div>
          <div class="my-2">
            {{ $t('routes.node.taskCanTakeSomeTime') }}
          </div>

          <div class="mb-2 text-body-2 text-medium-emphasis">
            {{ $t('routes.pod.extractArchivePasswordHint') }}
          </div>

          <v-text-field
            v-model="password"
            type="password"
            :label="$t('routes.pod.menuAccesslink.password')"
            :placeholder="$t('routes.pod.menuAccesslink.password')"
            :aria-label="$t('routes.pod.menuAccesslink.password')"
            autocomplete="new-password"
            variant="solo-filled"
            hide-details="auto"
            class="mb-4"
            data-cy="dialog-extractArchive-password"
          />

          <v-alert
            v-if="errorMessage"
            type="error"
            variant="tonal"
            class="mb-4"
            data-cy="dialog-extractArchive-error"
          >
            {{ errorMessage }}
          </v-alert>

          <UtilityPodsErrors></UtilityPodsErrors>

          <v-alert
            v-if="errorTranslatable"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ $replaceT(errorTranslatable.code, errorTranslatable.params, errorTranslatable.content) }}
          </v-alert>

          <div v-if="conflictingPaths" class="my-4 text-warning">
            <v-icon>mdi-alert</v-icon>
            {{ $t('routes.pod.extractArchiveConflictingDirectoriesWarning') }}
          </div>
          <v-list v-if="conflictingPaths" density="compact" max-height="600">
            <v-list-item
              v-for="(path, idx) in conflictingPaths"
              :key="`conflicting_path_${idx}`"
            >
              <v-list-item-title class="mr-1" style="text-overflow: unset; word-break: break-all; white-space: wrap">
                {{ path }}
              </v-list-item-title>
              <template v-slot:append>
                <v-select
                  v-model="conflictingPathsResolutions[path]"
                  :items="[
                    { title: this.$t('common.skip'), value: 'skip' },
                    { title: this.$t('common.rename'), value: 'rename' },
                    { title: this.$t('common.merge'), value: 'merge' }
                  ]"
                  min-width="170"
                  hide-details
                  density="compact"
                  variant="solo"
                />
              </template>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            :disabled="isRequestSubmitted"
            @click="extractArchive"
            color="primary"
            variant="elevated"
            data-cy="dialog-extractArchive-ok"
          >
            {{ $t('common.extract') }}
          </v-btn>
          <v-btn
            :disabled="isRequestSubmitted"
            variant="text"
            @click="dialog = false"
            data-cy="dialog-extractArchive-false"
          >
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>

  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useServiceStore } from '@/stores/ServiceStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import { fromPairs } from 'lodash';

export default {
  mixins: [DialogActionKeys],
  name: "ExtractArchiveDialog",
  emits: ['update:modelValue'],
  props: {
    pod: {
      type: Object,
      required: true,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      errorMessage: "",
      errorTranslatable: null,
      password: "",

      shouldDeletePod: true,
      isRequestSubmitted: false,
      conflictingPathsResolutions: null,
    };
  },
  watch: {
    modelValue() {
      this.conflictingPathsResolutions = null;
      this.password = "";
    },
  },
  methods: {
    handleFormSubmit() {
      if (this.isRequestSubmitted) {
        return;
      }
      this.extractArchive();
    },
    handleKeyDown(event) {
      if (!this.isRequestSubmitted && (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27)) {
        this.$emit('update:modelValue', false);
      }
    },
    async extractArchive() {
      this.errorMessage = "";
      this.errorTranslatable = null;

      const data = {
        id: this.pod.id,
        conflictResolutions: this.conflictingPathsResolutions,
        password: this.password,
      };

      this.isRequestSubmitted = true;

      const podStore = usePodStore();
      try {
        await podStore.extractArchive(data);

        if (data.conflictResolutions !== 'skip') {
          const nodeStore = useNodeStore();
          await nodeStore.getFullNodeInfo(this.pod.nodeid);
        }

        if (this.utilityPodsErrors.length === 0) {
          this.$emit('update:modelValue', false);
        }
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          const apiErrorStatus = error.response.data.error.status;
          const apiError = error.response.data.error.message;

          if (apiErrorStatus === 409) {
            this.conflictingPathsResolutions = fromPairs(error.response.data.error.conflictingPaths.map((v) => [ v, 'rename' ]));
          } else if (typeof apiError === 'object') {
            this.errorTranslatable = apiError;
          } else {
            this.errorMessage = apiError;
          }
        } else {
          this.errorMessage = 'Error!';
        }
      } finally {
        this.isRequestSubmitted = false;
      }
    },
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      },
    },
    conflictingPaths() {
      if (!this.conflictingPathsResolutions) {
        return null;
      }

      return Object.keys(this.conflictingPathsResolutions);
    },
    utilityPodsErrors() {
      const serviceStore = useServiceStore();
      return serviceStore.getUtilityPodsErrors;
    },
  },
};
</script>
